import { eventQBus } from "@otto-ec/global-resources/event-q-bus";
import { createLogger } from "utils/logging";
import { ActivationClient, ActivationResponseType } from "utils/activation";
import { ActivationStatus, buildDetailview, type Detailview } from "./Detailview";
import { ActivationTrigger } from "./events/ActivationTrigger";
import type { AddToBasketEvent } from "./events/AddToBasketEvent";
import type { TrackingService } from "./tracking/TrackingService";
import { attributeNameBenefitId, type DetailviewElement } from "./detailview_component";
import type { HTMLOcSnackbarV1Element } from "@otto-ec/otto-components/snackbar";
import { triggerHapticFeedback } from "@otto-ec/apps-api/hapticfeedback";
import { WishlistActivationBenefitType } from "./events/WishlistActivationEvent";
import { benefitEvents } from "@otto-ec/nexus-registry/benefit/events";
import { benefitWishlist } from "@otto-ec/nexus-registry/benefit/wishlist";
import { benefitSheet } from "@otto-ec/nexus-registry/benefit/sheet";
import { defineEvent } from "@otto-ec/global-resources/nexus";

const log = createLogger("tag", "detailview.service");

/*                                                                                                    */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;

/*                                                                                              */
const ACTIVATION_TIMEOUT_LOADER = 400;

/*                                                                                      */
if (!benefitEvents.isReady) {
	benefitEvents.assignNamespace({
		active: defineEvent(),
	});
}
if (!benefitWishlist.isReady) {
	benefitWishlist.assignNamespace({
		active: defineEvent(),
	});
}

export const wait = (timeout: number): Promise<void> => {
	return new Promise((resolve) => setTimeout(resolve, timeout));
};

/*                                                                                       */
/*                                                                                       */
/*                                                                                               */
const sentBenefitsToWishlistConsumer = new Set<string>();

export class DetailviewService {
	constructor(private readonly trackingService: TrackingService) {}

	private readonly activationClient = new ActivationClient();

	public handleNoContentDetailview(variationId: string, customComponentElement: DetailviewElement) {
		/*                                                                                                          */
		/*                                              */
		/*                                                                                                            */
		/*                                                                                                               */
		/*            */
		/*                                                                                                            */
		/*                                        */
		/*                                                                */
		const benefitId = customComponentElement.getAttribute(attributeNameBenefitId);
		if (benefitId) {
			sendActivationEventToPDPConsumer(benefitId, undefined, variationId, ActivationTrigger.INCORRECTLY_ASSUMED, false);
		}
	}

	public initialize(variationId: string, customComponentElement: DetailviewElement, eventMergeId: string | null) {
		const detailviewHtmlElement = customComponentElement.querySelector<HTMLDivElement>(`[data-module="ft9-benefit-detailview-${variationId}"]`);

		if (!detailviewHtmlElement) {
			return;
		}

		/*                                          */
		const actionContainer = detailviewHtmlElement.querySelector<HTMLDivElement>(`[data-module="ft9-benefit-detailview-actions-${variationId}"]`);
		if (!actionContainer) {
			log.error("action container not found");
			return;
		}

		const detailview = buildDetailview(detailviewHtmlElement, actionContainer, variationId);
		if (!detailview) {
			return;
		}

		this.handleAlreadyActivatedBenefit(detailview, customComponentElement.getAttribute(attributeNameBenefitId));
		initExpirationHandler(detailview, customComponentElement);

		if (detailview.activationStatus === ActivationStatus.DEACTIVATED || detailview.isEarlyAccess) {
			this.handleActivationFromOtherSources(detailview, customComponentElement);
		}

		log.info("Send load tracking event and set visible handler");
		this.trackingService.sendDetailviewBenefitLoadedEvent(detailview, eventMergeId);
		const toAppButtonElement = customComponentElement.querySelector<HTMLElement>("#benefit-detailview_app_btn");
		if (toAppButtonElement) {
			toAppButtonElement.addEventListener("click", () => {
				this.trackingService.sendDetailviewBenefitClickEvent(detailview);
			});
		}

		eventQBus.on("ft1.order-core.addToBasketResult", addToBasketHandler(this.trackingService, detailview).bind(this));

		const { activate } = window.otto.ft9.benefit.components.activationSlider;

		return activate.subscribe((event: any) => {
			if (!detailviewHtmlElement.contains(event.instance)) return;
			if (event.instance.dataset.benefitId !== detailview.benefitId) return;

			const activationUrl = event.instance.dataset.activationUrl;
			if (!activationUrl) {
				log.error("Missing activationUrl");
				return;
			}

			const errorLoggingPayload = {
				benefit_id: detailview.benefitId,
				benefit_active_until: detailview.activeUntil,
			};

			const startedMs = new Date().getMilliseconds();

			this.activationClient.activateByUrl(activationUrl, errorLoggingPayload, "tag").then(async (activationResponse) => {
				const endedMs = new Date().getMilliseconds();

				/*                                                                                                                                                */
				const LOADING_STATE_TIMEOUT_MS = Math.max(0, ACTIVATION_TIMEOUT_LOADER + 200 - (endedMs - startedMs));

				const eventMergeId = this.trackingService.sendDetailviewBenefitSwipeEvent(detailview, activationResponse);

				switch (activationResponse) {
					case ActivationResponseType.Activated:
						await wait(LOADING_STATE_TIMEOUT_MS);
						event.instance.setAttribute("is-activated", "true");
						detailview.activationStatus = ActivationStatus.ACTIVATED;
						setActivatedHeadline(detailviewHtmlElement);

						triggerHapticFeedback();
						sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, variationId, ActivationTrigger.ACTIVATED_BY_USER, true);
						sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, true, eventMergeId);
						return;
					case ActivationResponseType.Conflict: {
						const snackbar = detailviewHtmlElement.querySelector<HTMLOcSnackbarV1Element>("#benefit-detailview_snackbar--conflict");
						if (snackbar) {
							snackbar.open = true;
						}

						await wait(LOADING_STATE_TIMEOUT_MS);
						event.instance.reset();
						return;
					}
					case ActivationResponseType.Error: {
						const snackbar = detailviewHtmlElement.querySelector<HTMLOcSnackbarV1Element>("#benefit-detailview_snackbar--generic-error");
						if (snackbar) {
							snackbar.open = true;
						}

						await wait(LOADING_STATE_TIMEOUT_MS);
						event.instance.reset();
						return;
					}
				}
			});
		});
	}

	private handleAlreadyActivatedBenefit(detailview: Detailview, benefitIdAlreadyActivatedByFT5: string | null) {
		if (detailview.activationStatus === ActivationStatus.ACTIVATED) {
			sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.FROM_DB_STORAGE, true);
		}
		if (benefitIdAlreadyActivatedByFT5 && detailview.activationStatus === ActivationStatus.DEACTIVATED) {
			/*                                                                                                          */
			/*                                              */
			/*                                                                                                            */
			/*                                                                                                               */
			/*            */
			/*                                                                                                             */
			/*                                        */
			/*                                                                */
			sendActivationEventToPDPConsumer(benefitIdAlreadyActivatedByFT5, undefined, detailview.variationId, ActivationTrigger.INCORRECTLY_ASSUMED, false);
		}
	}

	private handleActivationFromOtherSources(detailview: Detailview, detailviewElement: DetailviewElement) {
		/*                      */
		benefitSheet.active.subscribe(({ benefitId, reloadDelay }) => {
			if (!benefitId || (benefitId !== detailview.benefitId && benefitId !== detailview.frameId)) return;
			sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.ACTIVATED_BY_USER, true);
			sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, true);

			setTimeout(async () => {
				await detailviewElement.triggerReload();
			}, reloadDelay ?? 500); /*                                                             */
			/*                                                                                                            */
			/*                                                                                                             */
			/*               */
		});

		/*                                 */
		benefitEvents.active.subscribe(({ id, frameId, active, variationId, trigger }) => {
			if (!active || trigger !== ActivationTrigger.ACTIVATED_BY_USER) return;
			if (!variationId || variationId === detailview.variationId) return;

			const benefitId = id;

			const container = detailviewElement.querySelectorAll<HTMLDivElement>(`[data-module|="ft9-benefit-detailview-actions"]`);
			container.forEach((element) => {
				const benefitIdFromContainer = element.dataset.benefitId!;
				const frameIdFromContainer = element.dataset.frameId;

				const isSameBenefitId = benefitIdFromContainer === benefitId;
				const isSameFrameId = !!(frameIdFromContainer && frameId && frameIdFromContainer === frameId);

				if (isSameBenefitId || isSameFrameId) {
					const activationSlider = element.querySelector("ft9-benefit-activation-slider-v1");
					if (activationSlider && !activationSlider.hasAttribute("is-activated")) {
						activationSlider.setAttribute("is-activated", "true");
						detailview.activationStatus = ActivationStatus.ACTIVATED;
						setActivatedHeadline(detailviewElement);
					}
				}
			});
		});
	}
}

function initExpirationHandler(detailview: Detailview, detailviewElement: DetailviewElement) {
	const end: Date = new Date(detailview.activeUntil);
	const delayInMillis: number = Math.min(Math.max(end.getTime() - new Date().getTime() - 1000, 1), MAX_CLEANUP_PERIOD_IN_MILLIS);

	log.info(`Expiration delay: ${delayInMillis} ms`);

	window.setTimeout(() => {
		/*                                                                                */
		if (detailview.isEarlyAccess) {
			window.setTimeout(async () => {
				await detailviewElement.triggerReload();
			}, 1_000);
			return;
		}
		detailview.activationStatus = ActivationStatus.DEACTIVATED;
		sendActivationEventToPDPConsumer(detailview.benefitId, detailview.frameId, detailview.variationId, ActivationTrigger.EXPIRED_BENEFIT, false);
		sendActivationEventToWishlistConsumer(detailview.benefitId, detailview.frameId, false);

		window.setTimeout(async () => {
			await detailviewElement.triggerReload();
		}, 10_000);
	}, delayInMillis);
}

/*                                                            */
function sendActivationEventToPDPConsumer(benefitId: string, frameId: string | undefined, variationId: string, trigger: ActivationTrigger, active: boolean) {
	benefitEvents.active.emit({
		/*                                                   */
		id: benefitId, /*                     */
		frameId: frameId, /*          */
		active: active,
		variationId: variationId, /*                     */
		trigger: trigger,
	});
}

/*  */
/*                                                                         */
export function sendActivationEventToWishlistConsumer(benefitId: string, frameId: string | undefined, active: boolean, eventMergeId?: string) {
	const activatedId = frameId || benefitId;
	if (sentBenefitsToWishlistConsumer.has(activatedId)) return;

	let type: WishlistActivationBenefitType;
	if (frameId === undefined) {
		type = WishlistActivationBenefitType.PARTNER;
	} else {
		type = WishlistActivationBenefitType.PLATFORM;
	}

	benefitWishlist.active.emit({
		id: activatedId,
		type,
		active: active,
		eventMergeId,
	});
	sentBenefitsToWishlistConsumer.add(activatedId);
}

export function addToBasketHandler(trackingService: TrackingService, detailview: Detailview) {
	return (event: AddToBasketEvent) => {
		if (!event.variationId || !event.eventMergeId) {
			return;
		}
		if (event.variationId !== detailview.variationId) {
			/*                                                                               */
			/*                                                                                                 */
			return;
		}
		trackingService.sendDetailviewBenefitMergeEvent(detailview, event.eventMergeId);
	};
}

function setActivatedHeadline(detailviewHtmlElement: HTMLElement) {
	const headlineElement = detailviewHtmlElement.querySelector<HTMLElement>("[data-headline-activated]");
	if (!headlineElement) return;
	headlineElement.textContent = headlineElement.dataset.headlineActivated!;
}
